import type { ReactElement, MouseEvent } from "react";
import type { StoryVersion } from "@/types/story";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BookOpen,
  Sparkles,
  Loader2,
  ArrowRight,
  Star,
  Lock,
  MoreHorizontal,
  Eye,
  RefreshCw,
  Wand2,
  X,
} from "lucide-react";
import VersionBadge from "@/components/VersionBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Story data structure
 */
interface Story {
  id: string;
  title: string;
  prompt?: string;
  age_range: string;
  category?: string;
  is_public?: boolean;
  thumbnail?: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  created_at: string;
}

/**
 * Job status type
 */
type JobStatus = "queued" | "processing" | "completed" | "failed";

/**
 * Job data structure
 */
interface Job {
  id: string;
  status: JobStatus;
  prompt?: string;
  progress?: number;
  queue_position?: number;
  thumbnail_url?: string;
  story_id?: string;
}

/**
 * Props for MyStoriesWorkspace component
 */
interface MyStoriesWorkspaceProps {
  onStoryClick?: (storyId: string) => void;
  onJobClick?: (jobId: string) => void;
  onRecreate?: (story: Story) => void;
  onReimagine?: (story: Story) => void;
}

/**
 * Stories API response
 */
interface StoriesResponse {
  stories: Story[];
  total: number;
}

/**
 * Jobs API response
 */
interface JobsResponse {
  jobs: Job[];
}

/**
 * Props for InProgressCard sub-component
 */
interface InProgressCardProps {
  job: Job;
}

/**
 * Props for CompactStoryCard sub-component
 */
interface CompactStoryCardProps {
  story: Story;
}

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * MyStoriesWorkspace Component
 *
 * Displays user's stories and active generation jobs in a compact workspace view.
 */
const MyStoriesWorkspace = ({
  onStoryClick,
  onJobClick,
  onRecreate,
  onReimagine,
}: MyStoriesWorkspaceProps): ReactElement => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  // Grace period counter to prevent jobs from disappearing on API hiccups
  const pollGraceCounter = useRef<number>(0);

  // Load initial data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Poll for job updates when there are active jobs
  useEffect(() => {
    if (activeJobs.length === 0) return;

    const pollInterval = setInterval(() => {
      loadJobs();
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [activeJobs.length]);

  const loadData = async (): Promise<void> => {
    setLoading(true);
    await Promise.all([loadStories(), loadJobs()]);
    setLoading(false);
  };

  const loadStories = async (): Promise<void> => {
    try {
      const response = await axios.get<StoriesResponse>(
        `${API}/v1/stories/library?limit=10&offset=0&sort=newest`,
        { withCredentials: true }
      );
      setStories(response.data.stories || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to load stories:", error);
    }
  };

  const loadJobs = async (): Promise<void> => {
    try {
      // Don't use .catch() that returns empty arrays - let errors propagate
      // so we keep previous state on network hiccups instead of clearing jobs
      const [activeJobsRes, allJobsRes] = await Promise.all([
        axios.get<JobsResponse>(`${API}/v1/jobs/mine?include_completed=false`, {
          withCredentials: true,
        }),
        axios.get<JobsResponse>(`${API}/v1/jobs/mine?include_completed=true&limit=5`, {
          withCredentials: true,
        }),
      ]);

      const allJobs = allJobsRes.data.jobs || [];
      const active = (activeJobsRes.data.jobs || []).filter(
        (j) => j.status === "queued" || j.status === "processing"
      );

      // Grace period logic: only apply when jobs disappear unexpectedly (not completed)
      // This prevents jobs from disappearing due to momentary API hiccups
      if (active.length === 0 && activeJobs.length > 0) {
        // Check if our previously active jobs are now in completed/failed state
        const previousJobIds = activeJobs.map((j) => j.id);
        const completedOrFailed = allJobs.filter(
          (j) => previousJobIds.includes(j.id) && (j.status === "completed" || j.status === "failed")
        );

        // If jobs legitimately completed/failed, no grace period needed
        if (completedOrFailed.length === activeJobs.length) {
          pollGraceCounter.current = 0;
        } else {
          // Jobs disappeared without completing - apply grace period
          pollGraceCounter.current += 1;
          if (pollGraceCounter.current < 5) {
            console.log(`Job grace period: poll ${pollGraceCounter.current}/5 (jobs disappeared unexpectedly)`);
            return;
          }
        }
      } else {
        pollGraceCounter.current = 0;
      }

      setActiveJobs(active);

      // Check if any job completed - reload stories if so
      const completed = allJobs.filter(
        (j) => j.status === "completed" && j.story_id
      );
      if (completed.length > 0) {
        loadStories();
      }
    } catch (error) {
      // On error, keep the previous activeJobs state - don't clear
      // This prevents jobs from disappearing on network hiccups
      console.error("Failed to load jobs (keeping previous state):", error);
    }
  };

  const handleStoryClick = (storyId: string): void => {
    if (onStoryClick) {
      onStoryClick(storyId);
    } else {
      navigate(`/story/${storyId}`);
    }
  };

  const handleJobClick = (jobId: string): void => {
    if (onJobClick) {
      onJobClick(jobId);
    } else {
      navigate(`/generating/${jobId}`);
    }
  };

  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [jobToCancel, setJobToCancel] = useState<string | null>(null);

  const openCancelDialog = (jobId: string, e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    setJobToCancel(jobId);
    setShowCancelDialog(true);
  };

  const handleCancelJob = async (): Promise<void> => {
    if (!jobToCancel) return;

    setCancellingJobId(jobToCancel);
    try {
      await axios.delete(`${API}/v1/jobs/${jobToCancel}`, {
        withCredentials: true,
      });
      toast.success("Story cancelled", {
        description: "No credits were charged.",
      });
      // Remove from active jobs immediately
      setActiveJobs((prev) => prev.filter((j) => j.id !== jobToCancel));
      setShowCancelDialog(false);
      setJobToCancel(null);
    } catch (error) {
      console.error("Failed to cancel job:", error);
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const message =
        axiosError.response?.data?.detail || "Failed to cancel story";
      toast.error(message);
    } finally {
      setCancellingJobId(null);
    }
  };

  // In-Progress Job Card (compact version)
  const InProgressCard = ({ job }: InProgressCardProps): ReactElement => (
    <div
      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer group hover:bg-orange-50/50 dark:hover:bg-white/10 transition-all"
      onClick={() => handleJobClick(job.id)}
      style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
    >
      {/* Thumbnail with spinner */}
      <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
        {job.thumbnail_url ? (
          <img
            src={getImageUrl(job.thumbnail_url) || undefined}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center animate-pulse"
            style={{
              background: "linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: "var(--st-primary-hover)" }} />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className="text-xs font-bold"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            Creating...
          </span>
          <span
            className="px-1.5 py-0.5 rounded-full text-xs font-semibold animate-pulse"
            style={{
              background: "var(--st-border-light)",
              color: "var(--st-primary)",
              fontSize: "10px",
            }}
          >
            {job.status === "queued"
              ? `#${job.queue_position}`
              : `${job.progress || 0}%`}
          </span>
        </div>
        <p
          className="text-xs truncate"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
        >
          {job.prompt && job.prompt.length > 40
            ? job.prompt.substring(0, 40) + "..."
            : job.prompt}
        </p>
        {/* Mini progress bar */}
        {job.status === "processing" && (
          <div
            className="mt-1.5 h-1 rounded-full overflow-hidden"
            style={{ background: "var(--st-border-light)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${job.progress || 0}%`,
                background: "linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
              }}
            />
          </div>
        )}
      </div>

      {/* Cancel button - shows on hover */}
      <button
        onClick={(e) => openCancelDialog(job.id, e)}
        disabled={cancellingJobId === job.id}
        className="p-1.5 rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-100 transition-all flex-shrink-0"
        title="Cancel story"
      >
        {cancellingJobId === job.id ? (
          <Loader2
            className="w-4 h-4 animate-spin"
            style={{ color: "#EF4444" }}
          />
        ) : (
          <X className="w-4 h-4" style={{ color: "#EF4444" }} />
        )}
      </button>
    </div>
  );

  // Compact Story Card
  const CompactStoryCard = ({ story }: CompactStoryCardProps): ReactElement => (
    <div className="flex items-center gap-3 p-2.5 rounded-xl group hover:bg-black/5 dark:hover:bg-white/10 transition-all">
      {/* Thumbnail - clickable */}
      <div
        className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
        onClick={() => handleStoryClick(story.id)}
      >
        {story.thumbnail ? (
          <img
            src={getImageUrl(story.thumbnail) || undefined}
            alt={story.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
            }}
          >
            <Sparkles className="w-4 h-4" style={{ color: "var(--st-primary-hover)" }} />
          </div>
        )}
      </div>

      {/* Content - clickable */}
      <div
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => handleStoryClick(story.id)}
      >
        <div className="flex items-center gap-1.5">
          <h3
            className="font-bold text-sm truncate"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            {story.title}
          </h3>
          <VersionBadge
            version={story.version}
            isProStory={story.is_pro_story}
            size="small"
          />
          {story.is_public === false && (
            <Lock
              className="w-3 h-3 flex-shrink-0"
              style={{ color: "var(--st-text-muted)" }}
            />
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-xs"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            {story.category || "general"}
          </span>
          <span className="text-xs" style={{ color: "var(--st-text-muted)" }}>•</span>
          <span
            className="text-xs"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            {story.age_range}
          </span>
        </div>
      </div>

      {/* More Menu */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1.5 rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-all focus:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44" sideOffset={5}>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleStoryClick(story.id);
            }}
            className="cursor-pointer"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Story
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onRecreate?.(story);
            }}
            className="cursor-pointer"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onReimagine?.(story);
            }}
            className="cursor-pointer"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            New Version
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  // Empty State
  const EmptyState = (): ReactElement => (
    <div className="text-center py-8 px-4">
      <div
        className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
        }}
      >
        <BookOpen className="w-6 h-6" style={{ color: "var(--st-primary-hover)" }} />
      </div>
      <p
        className="font-bold text-sm mb-1"
        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
      >
        No stories yet!
      </p>
      <p
        className="text-xs"
        style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
      >
        Create your first magical story using the form.
      </p>
    </div>
  );

  // Loading State
  if (loading && stories.length === 0 && activeJobs.length === 0) {
    return (
      <div
        className="rounded-2xl p-6 shadow-sm"
        style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
          <h2
            className="text-lg font-bold"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            My Stories
          </h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--st-primary)" }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-sm overflow-hidden"
      style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 border-b"
        style={{ borderColor: "var(--st-border-light)" }}
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
          <h2
            className="text-lg font-bold"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            My Stories
          </h2>
        </div>
        {total > 0 && (
          <span
            className="text-xs px-2 py-1 rounded-full"
            style={{
              background: "var(--st-border-light)",
              color: "var(--st-text-muted)",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {total} {total === 1 ? "story" : "stories"}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="max-h-[500px] overflow-y-auto">
        {/* Active Jobs Section */}
        {activeJobs.length > 0 && (
          <div
            className="p-3 border-b"
            style={{ borderColor: "var(--st-border-light)", background: "var(--st-bg-surface)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
              <span
                className="text-xs font-semibold"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Creating
              </span>
            </div>
            <div className="space-y-2">
              {activeJobs.map((job) => (
                <InProgressCard key={job.id} job={job} />
              ))}
            </div>
          </div>
        )}

        {/* Stories List */}
        {stories.length > 0 ? (
          <div className="p-2">
            {stories.map((story) => (
              <CompactStoryCard key={story.id} story={story} />
            ))}

            {/* View All Link */}
            {total > stories.length && (
              <button
                onClick={() => navigate("/library")}
                className="w-full mt-2 py-2.5 text-center text-sm font-medium rounded-xl hover:bg-orange-50 dark:hover:bg-white/10 transition-colors"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-primary)" }}
              >
                View all {total} stories →
              </button>
            )}
          </div>
        ) : activeJobs.length === 0 ? (
          <EmptyState />
        ) : null}
      </div>

      {/* Cancel Story Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={(open: boolean) => {
          setShowCancelDialog(open);
          if (!open) setJobToCancel(null);
        }}
        title="Cancel Story?"
        description="This story is currently being generated. Cancelling will stop the process and no credits will be charged."
        confirmText="Cancel Story"
        cancelText="Keep Creating"
        onConfirm={handleCancelJob}
        loading={cancellingJobId !== null}
        variant="warning"
      />
    </div>
  );
};

export default MyStoriesWorkspace;
